import * as THREE from 'three';
import * as materials from '../core/materials.js';
import * as audio from './audio.js';

/* ------------------------------------------------------------------
   Puutarhurin Painajainen — FPS-versio.

   Pelitasapaino (globaali ongelmageneraattori, vaikeuskäyrä, pisteytys)
   on portattu sellaisenaan pelin aiemmasta yhden-tiedoston versiosta
   (ks. ../../index.html.v1-single-file.bak) — se oli jo hiottu ja
   headless-testattu, joten sitä ei keksitty uudelleen.

   Uutta: ensimmäisen persoonan tähtäinpohjainen työkaluvuorovaikutus
   (kannu/sakset/suihke, raycast ruudun keskeltä) korvaa aiemman
   "kävele lähelle + pidä nappia" -mekaniikan, plus rajallinen vesisäiliö
   joka pakottaa käymään kaivolla.
------------------------------------------------------------------ */

const SAVE_KEY = 'puutarhurin_painajainen_fps_v1';

const RIVIT = 3, SARAKKEET = 4;
const VALI_X = 2.6, VALI_Z = 2.8;
const KASVI_VARIT = [0xe74c3c, 0xf1c40f, 0x9b59b6, 0xe67e22, 0xff6f91, 0xf39c12];

// Ei kelloa vastaan pelaamista: kiinteä, kohtuullinen tahti koko pelin ajan
// (ei vaikeuskäyrää) — vaikeus tulee tehtävistä, ei ajan kulumisesta.
const MAX_YHTAAIKAISET = 3;
const SPAWN_VALI_MIN = 3.0, SPAWN_VALI_MAX = 4.5;
const RATKAISUAIKA = { kuiva: 9, rikkaruoho: 10.5, tuholainen: 8 };
const TYON_KESTO = { kuiva: 1.1, rikkaruoho: 1.6, tuholainen: 2.0 };
const PISTEET = { kuiva: 8, rikkaruoho: 10, tuholainen: 14 };
const RAPAUTUMISNOPEUS = 0.5;
const UUDELLEENKASVYAIKA = 4;
const ONGELMATYYPIT = ['kuiva', 'rikkaruoho', 'tuholainen'];

const TAHTAYS_ETAISYYS = 4.5;
const LAHIETAISYYS = 1.7; // seisot niin lähellä että penkki/kaivo kelpaa kohteeksi vaikka tähtäin ei osu
const VAINO_LAHIETAISYYS = 2.0; // NPC:lle hieman löysempi kuin penkeille, mutta ei niin löysä
// että "puhu Väinölle" -läheisyys nappaisi lähistöllä kasvia hoitavan kosketuksen (ks. onToolButtonDown)
const TYOKALU_KOHTEELLE = { kuiva: 'kannu', rikkaruoho: 'sakset', tuholainen: 'suihke' };
const TYOKALU_NIMI = { kannu: 'Kastelukannu', sakset: 'Sakset', suihke: 'Tuholaissuihke' };
// Yksi väri per ongelma -> kelluva ikoni penkin yllä kertoo suoraan mitä työkalua tarvitaan.
const ONGELMA_VARI = { kuiva: 0x4aa3e8, rikkaruoho: 0x6bb23a, tuholainen: 0xe74c3c };
const VESI_MAARA_ALKU = 6;
const VESI_KAPASITEETTI_KASVU = 4; // tehtävä 3:n palkinto: isompi kannu
const TAYTTOAIKA = 1.4;

// ========================== TARINA: VÄINÖN TEHTÄVÄT ==========================
// Ei loppumatonta selviytymistä — neljä tehtävää, selkeä alku ja loppu.
// "tyyppi" kertoo miten questiEdistyminen kasvaa (ks. paivitaTehtavanEdistyminen).
const VAINO_PENKKI_IDX = 5; // "mummon suosikki" — tietty penkki, ks. tehtävä 2
const TEHTAVAT = [
  {
    otsikko: 'Tervetuloa kotiin',
    vainonSanat: 'No terve! Mummosi puutarha on kaivannut käsipareja. Ratkaise muutama ongelma, niin näytän lisää.',
    tavoite: 'Ratkaise 3 ongelmaa',
    maara: 3,
    tyyppi: 'ratkaisuja',
    palkinto: 'Väinö hymyilee: "Hyvä alku! Jatka samaan malliin."',
  },
  {
    otsikko: 'Mummon suosikki',
    vainonSanat: 'Tuo nurkan penkki oli mummosi ylpeys — tomaatit vetivät aina tuholaisia. Pidä se hengissä.',
    tavoite: 'Torju tuholainen samalta penkiltä 3 kertaa',
    maara: 3,
    tyyppi: 'suojattuPenkki',
    palkinto: 'Se oli hänen lempikasvinsa. Kiitos, että pidit siitä huolta.',
  },
  {
    otsikko: 'Kuiva kesä',
    vainonSanat: 'Muistan kesän jolloin kaivo melkein kuivui. Pidä kannu täynnä ja kastele ahkerasti.',
    tavoite: 'Kastele onnistuneesti 8 kertaa',
    maara: 8,
    tyyppi: 'kasteluja',
    palkinto: 'Väinö antaa ison kastelukannun — säiliö kasvaa.',
  },
  {
    otsikko: 'Puutarhajuhlat',
    vainonSanat: 'Kylän puutarhajuhlat lähestyvät. Näytetään kaikille millainen tästä paikasta tuli.',
    tavoite: 'Kaikki penkit kunnossa yhtä aikaa',
    tyyppi: 'kaikkiKunnossa',
    palkinto: 'Puutarhajuhlat onnistuivat!',
  },
];

// ========================== TALLENNUS ==========================
function luoTallennus(avain, oletus) {
  let data = { ...oletus };
  try {
    const raaka = localStorage.getItem(avain);
    if (raaka) data = { ...oletus, ...JSON.parse(raaka) };
  } catch (e) { /* ei tallennusta käytössä */ }
  return {
    get: (k) => data[k],
    set(k, v) {
      data[k] = v;
      try { localStorage.setItem(avain, JSON.stringify(data)); } catch (e) { /* ohita */ }
    }
  };
}
const tallennus = luoTallennus(SAVE_KEY, { ennatys: 0 });

// ========================== APUFUNKTIOT (visuaali) ==========================
function lahesty(nykyinen, tavoite, nopeus, dt) {
  const ero = tavoite - nykyinen;
  const maxAskel = nopeus * dt;
  if (Math.abs(ero) <= maxAskel) return tavoite;
  return nykyinen + Math.sign(ero) * maxAskel;
}

function tahtaaPelaajaan(ryhma, maailmanPos, camera) {
  const angle = Math.atan2(camera.position.x - maailmanPos.x, camera.position.z - maailmanPos.z);
  ryhma.rotation.set(0, angle, 0);
}

// ========================== KASVI/PENKKI-RAKENNUSPALIKAT ==========================
function luoKasvi(hedelmaVari) {
  const ryhma = new THREE.Group();
  const lehtiMat = new THREE.MeshStandardMaterial({ color: 0x4c8c3b, roughness: 0.9 });
  const lehtiGeom = new THREE.IcosahedronGeometry(0.3, 0);
  for (const [x, y, z] of [[0, 0.32, 0], [0.18, 0.22, 0.15], [-0.2, 0.22, -0.12], [0.05, 0.42, -0.18]]) {
    const m = new THREE.Mesh(lehtiGeom, lehtiMat);
    m.position.set(x, y, z);
    m.castShadow = true;
    ryhma.add(m);
  }
  const hedelmaMat = new THREE.MeshStandardMaterial({ color: hedelmaVari, roughness: 0.5 });
  const hedelmaGeom = new THREE.SphereGeometry(0.1, 8, 8);
  for (const [x, y, z] of [[0.15, 0.35, 0.2], [-0.12, 0.3, 0.15], [0.05, 0.5, -0.1]]) {
    const m = new THREE.Mesh(hedelmaGeom, hedelmaMat);
    m.position.set(x, y, z);
    m.castShadow = true;
    ryhma.add(m);
  }
  return { ryhma, lehtiMat };
}

function luoRikkaruoho() {
  const ryhma = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0x6b7a2a, roughness: 1 });
  for (const [x, z] of [[0, 0], [0.15, 0.1], [-0.15, -0.05], [0.05, -0.18], [-0.1, 0.15]]) {
    const h = 0.4 + Math.random() * 0.25;
    const m = new THREE.Mesh(new THREE.ConeGeometry(0.05, h, 6), mat);
    m.position.set(x, h / 2, z);
    m.rotation.z = (Math.random() - 0.5) * 0.3;
    m.castShadow = true;
    ryhma.add(m);
  }
  return ryhma;
}

function luoTuholainen() {
  const ryhma = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.4 });
  const runko = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 8), mat);
  runko.scale.set(1, 0.7, 1.3);
  ryhma.add(runko);
  for (const s of [-1, 1]) {
    for (let i = 0; i < 3; i++) {
      const jalka = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.12, 4), mat);
      jalka.position.set(s * 0.08, -0.04, -0.06 + i * 0.06);
      jalka.rotation.z = s * 0.7;
      ryhma.add(jalka);
    }
  }
  ryhma.position.y = 0.35;
  ryhma.visible = false;
  return ryhma;
}

// Kolme kelluvaa työkalukuvaketta penkin yllä — piirretty canvasille pikto-
// grammeina (ei 3D-verkkoina): näkyvät selkeinä kulmasta/etäisyydestä riippumatta
// ja kääntyvät automaattisesti kameraan päin (THREE.Sprite). Näkyy vain kun
// penkillä on vastaava aktiivinen ongelma.
function luoIkoniTekstuuri(piirra) {
  const koko = 128;
  const cnv = document.createElement('canvas');
  cnv.width = cnv.height = koko;
  const ctx = cnv.getContext('2d');
  ctx.translate(koko / 2, koko / 2);
  piirra(ctx, koko);
  const tex = new THREE.CanvasTexture(cnv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function tekstuuriKannu() {
  return luoIkoniTekstuuri((ctx, k) => {
    const vari = '#4aa3e8';
    ctx.fillStyle = vari; ctx.strokeStyle = '#0d2b40'; ctx.lineWidth = k * 0.035;
    ctx.beginPath(); ctx.ellipse(-k * 0.03, k * 0.06, k * 0.24, k * 0.19, 0, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(k * 0.18, -k * 0.06); ctx.lineTo(k * 0.40, -k * 0.20);
    ctx.lineTo(k * 0.36, -k * 0.06); ctx.lineTo(k * 0.20, k * 0.04);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.beginPath();
    ctx.arc(-k * 0.05, -k * 0.02, k * 0.20, Math.PI * 1.05, Math.PI * 1.75);
    ctx.lineWidth = k * 0.055; ctx.strokeStyle = vari;
    ctx.stroke();
    ctx.strokeStyle = '#0d2b40'; ctx.lineWidth = k * 0.02; ctx.stroke();
  });
}

function tekstuuriSakset() {
  return luoIkoniTekstuuri((ctx, k) => {
    const vari = '#6bb23a';
    ctx.strokeStyle = '#173a08'; ctx.fillStyle = vari;
    for (const suunta of [1, -1]) {
      ctx.save();
      ctx.rotate(suunta * 0.55);
      ctx.lineWidth = k * 0.02;
      ctx.beginPath();
      ctx.moveTo(-k * 0.34, 0);
      ctx.lineTo(k * 0.06, -k * 0.05);
      ctx.lineTo(k * 0.06, k * 0.05);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.arc(-k * 0.32, 0, k * 0.09, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
      ctx.restore();
    }
    ctx.beginPath(); ctx.arc(0, 0, k * 0.035, 0, Math.PI * 2);
    ctx.fillStyle = '#173a08'; ctx.fill();
  });
}

function tekstuuriSuihke() {
  return luoIkoniTekstuuri((ctx, k) => {
    const vari = '#e74c3c';
    ctx.fillStyle = vari; ctx.strokeStyle = '#4a0d06'; ctx.lineWidth = k * 0.03;
    ctx.beginPath(); ctx.roundRect(-k * 0.14, -k * 0.10, k * 0.28, k * 0.34, k * 0.06);
    ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.roundRect(-k * 0.06, -k * 0.28, k * 0.12, k * 0.2, k * 0.03);
    ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.roundRect(k * 0.14, -k * 0.05, k * 0.12, k * 0.12, k * 0.02);
    ctx.fill(); ctx.stroke();
    ctx.strokeStyle = vari; ctx.lineWidth = k * 0.025; ctx.lineCap = 'round';
    for (const [dx, dy] of [[0.02, -0.34], [-0.08, -0.30], [0.11, -0.28]]) {
      ctx.beginPath();
      ctx.moveTo(k * dx, k * dy);
      ctx.lineTo(k * dx - k * 0.03, k * dy - k * 0.07);
      ctx.stroke();
    }
  });
}

function luoTyokaluSprite(tex) {
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: true });
  mat.toneMapped = false; // pidä canvas-piirretyt värit kirkkaina, ei ACES-tonemapin himmentämiä
  const spr = new THREE.Sprite(mat);
  spr.scale.set(0.55, 0.55, 1);
  spr.visible = false;
  return spr;
}

const IKONI_TEKSTUURIT = { kannu: tekstuuriKannu(), sakset: tekstuuriSakset(), suihke: tekstuuriSuihke() };

function luoOngelmaIkonit() {
  const ryhma = new THREE.Group();
  ryhma.position.y = 1.7;
  ryhma.visible = false;
  const kannu = luoTyokaluSprite(IKONI_TEKSTUURIT.kannu);
  const sakset = luoTyokaluSprite(IKONI_TEKSTUURIT.sakset);
  const suihke = luoTyokaluSprite(IKONI_TEKSTUURIT.suihke);
  ryhma.add(kannu, sakset, suihke);
  return { ryhma, kannu, sakset, suihke };
}

function luoEdistymispalkki() {
  const ryhma = new THREE.Group();
  const tausta = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.14, 0.05), new THREE.MeshBasicMaterial({ color: 0x222222 }));
  ryhma.add(tausta);
  const tayttoGeom = new THREE.BoxGeometry(0.86, 0.1, 0.06);
  tayttoGeom.translate(0.43, 0, 0.01);
  const taytto = new THREE.Mesh(tayttoGeom, new THREE.MeshBasicMaterial({ color: 0x5ec878 }));
  taytto.position.x = -0.43;
  taytto.scale.x = 0.001;
  ryhma.add(taytto);
  ryhma.position.y = 1.9;
  ryhma.visible = false;
  return { ryhma, tausta, taytto };
}

function luoPenkki(rivi, sarake, alkuX, alkuZ) {
  const x = alkuX + sarake * VALI_X;
  const z = alkuZ + rivi * VALI_Z;
  const ryhma = new THREE.Group();
  ryhma.position.set(x, 0, z);

  const seed = 100 + rivi * SARAKKEET + sarake;
  const maaMat = materials.proseduraalinenMatta({ seed, tint: [0.34, 0.24, 0.16], damp: 0.35 });
  const maa = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.28, 1.6), maaMat);
  maa.position.y = 0.14;
  maa.receiveShadow = true;
  maa.castShadow = true;
  ryhma.add(maa);

  const kehysMat = new THREE.MeshStandardMaterial({ color: 0x3d2a1a, roughness: 0.9 });
  for (const [dx, dz, sx, sz] of [[0, 0.8, 1.7, 0.1], [0, -0.8, 1.7, 0.1], [0.8, 0, 0.1, 1.7], [-0.8, 0, 0.1, 1.7]]) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(sx, 0.32, sz), kehysMat);
    m.position.set(dx, 0.16, dz);
    m.castShadow = true;
    ryhma.add(m);
  }

  const kasviVari = KASVI_VARIT[(rivi * SARAKKEET + sarake) % KASVI_VARIT.length];
  const kasvi = luoKasvi(kasviVari);
  kasvi.ryhma.position.y = 0.28;
  ryhma.add(kasvi.ryhma);

  const rikkaruoho = luoRikkaruoho();
  rikkaruoho.position.y = 0.28;
  rikkaruoho.scale.set(0.001, 0.001, 0.001);
  ryhma.add(rikkaruoho);

  const tuholainen = luoTuholainen();
  ryhma.add(tuholainen);

  const ongelmaIkoni = luoOngelmaIkonit();
  ryhma.add(ongelmaIkoni.ryhma);

  const palkki = luoEdistymispalkki();
  ryhma.add(palkki.ryhma);

  const penkki = {
    ryhma, maa, maaMat, kasvi, rikkaruoho, tuholainen, ongelmaIkoni, palkki,
    tila: 'kasvaa', ongelma: null, ongelmaAika: 0, ongelmaMax: 0, edistyminen: 0,
    uudelleenkasvyAika: 0, kuivaVis: 0, rikkaVis: 0, satunnaisvaihe: Math.random() * 10,
  };
  maa.userData.penkki = penkki;
  return penkki;
}

function luoPuu(x, z) {
  const g = new THREE.Group();
  const runkoMat = new THREE.MeshStandardMaterial({ color: 0x6b4423, roughness: 1 });
  const runko = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 1.4, 8), runkoMat);
  runko.position.y = 0.7;
  runko.castShadow = true;
  g.add(runko);
  const lehvastoMat = new THREE.MeshStandardMaterial({ color: 0x2f6b2f, roughness: 1 });
  for (const [dy, r] of [[1.6, 0.75], [2.05, 0.55], [2.4, 0.38]]) {
    const m = new THREE.Mesh(new THREE.ConeGeometry(r, 0.9, 8), lehvastoMat);
    m.position.y = dy;
    m.castShadow = true;
    g.add(m);
  }
  g.position.set(x, 0, z);
  return g;
}

// ========================== HIUKKASET (poolattu) ==========================
function luoHiukkasTekstuuri() {
  const koko = 64;
  const cnv = document.createElement('canvas');
  cnv.width = cnv.height = koko;
  const c2d = cnv.getContext('2d');
  const g = c2d.createRadialGradient(koko / 2, koko / 2, 0, koko / 2, koko / 2, koko / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.7)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  c2d.fillStyle = g;
  c2d.fillRect(0, 0, koko, koko);
  return new THREE.CanvasTexture(cnv);
}

export function buildGame(scene, hooks = {}) {
  const group = new THREE.Group();
  scene.add(group);

  const colliders = [];

  // ---- Valaistus + sumu (aurinkoinen kesäpäivä) ------------------
  scene.background = new THREE.Color(0xaee2f7);
  scene.fog = new THREE.Fog(0xaee2f7, 20, 42);
  group.add(new THREE.HemisphereLight(0xffffff, 0x5a7f48, 0.75));
  const suuntavalo = new THREE.DirectionalLight(0xfff6da, 2.2);
  // Sijoitettu niin että aurinko näkyy pelaajan oletuskatseessa (yaw=PI, katsoo +Z:aan)
  // kohtuullisella korkeuskulmalla — ei suoraan sivulla tai lähes zeniitissä.
  suuntavalo.position.set(2, 8, 9);
  suuntavalo.target.position.set(0, 0, -2);
  group.add(suuntavalo.target);
  suuntavalo.castShadow = true;
  suuntavalo.shadow.mapSize.set(2048, 2048);
  suuntavalo.shadow.camera.left = -16; suuntavalo.shadow.camera.right = 16;
  suuntavalo.shadow.camera.top = 16; suuntavalo.shadow.camera.bottom = -16;
  suuntavalo.shadow.camera.near = 1; suuntavalo.shadow.camera.far = 34;
  suuntavalo.shadow.bias = -0.0015;
  suuntavalo.shadow.normalBias = 0.02;
  group.add(suuntavalo);

  // Näkyvä aurinkokiekko taivaalla auringon suunnassa — pelkkä valonlähde ilman
  // näkyvää kiekkoa ei "näy paistavan". Kaukana ja fogista riippumaton (fog:false),
  // jotta pysyy kirkkaana etäisyydestä huolimatta.
  function luoAurinko() {
    const koko = 256;
    const cnv = document.createElement('canvas');
    cnv.width = cnv.height = koko;
    const c2d = cnv.getContext('2d');
    const g = c2d.createRadialGradient(koko / 2, koko / 2, 0, koko / 2, koko / 2, koko / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.18, 'rgba(255,251,235,0.95)');
    g.addColorStop(0.45, 'rgba(255,244,210,0.35)');
    g.addColorStop(1, 'rgba(255,244,210,0)');
    c2d.fillStyle = g;
    c2d.fillRect(0, 0, koko, koko);
    const tex = new THREE.CanvasTexture(cnv);
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, fog: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(46, 46, 1);
    sprite.position.copy(suuntavalo.position).normalize().multiplyScalar(220);
    return sprite;
  }
  group.add(luoAurinko());

  // ---- Maa --------------------------------------------------------
  const nurmiMat = materials.proseduraalinenMatta({ seed: 5, tint: [0.34, 0.5, 0.26], repeat: 8 });
  const nurmi = new THREE.Mesh(new THREE.PlaneGeometry(24, 20), nurmiMat);
  nurmi.rotation.x = -Math.PI / 2;
  nurmi.receiveShadow = true;
  group.add(nurmi);

  // ---- Aita (kolliderit) --------------------------------------------
  const AITA_X = 11, AITA_Z = 9;
  const aitaMat = materials.proseduraalinenMatta({ seed: 20, tint: [0.42, 0.3, 0.18] });
  const aidat = [
    { pos: [0, 0.55, -AITA_Z], size: [AITA_X * 2, 1.1, 0.2] },
    { pos: [0, 0.55, AITA_Z], size: [AITA_X * 2, 1.1, 0.2] },
    { pos: [-AITA_X, 0.55, 0], size: [0.2, 1.1, AITA_Z * 2] },
    { pos: [AITA_X, 0.55, 0], size: [0.2, 1.1, AITA_Z * 2] },
  ];
  for (const a of aidat) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(...a.size), aitaMat);
    m.position.set(...a.pos);
    m.castShadow = true; m.receiveShadow = true;
    group.add(m);
    colliders.push(new THREE.Box3().setFromObject(m));
  }

  for (const [x, z] of [[-13, -10], [13, -10], [-13, 10], [13, 10], [0, -12], [0, 12]]) {
    group.add(luoPuu(x, z));
  }

  // ---- Penkit -------------------------------------------------------
  const alkuX = -(SARAKKEET - 1) * VALI_X / 2;
  const alkuZ = -(RIVIT - 1) * VALI_Z / 2 - 1;
  const penkit = [];
  const bedHitMeshes = [];
  for (let r = 0; r < RIVIT; r++) {
    for (let s = 0; s < SARAKKEET; s++) {
      const p = luoPenkki(r, s, alkuX, alkuZ);
      group.add(p.ryhma);
      penkit.push(p);
      bedHitMeshes.push(p.maa);
    }
  }

  // ---- Kaivo (vedentäyttö) -------------------------------------------
  const kaivoZ = alkuZ - 2.6;
  const kaivoMat = materials.proseduraalinenMatta({ seed: 30, tint: [0.5, 0.5, 0.52], damp: 0.25 });
  const kaivoRunko = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.65, 0.7, 16), kaivoMat);
  kaivoRunko.position.set(0, 0.35, kaivoZ);
  kaivoRunko.castShadow = true; kaivoRunko.receiveShadow = true;
  group.add(kaivoRunko);
  const puuMat = new THREE.MeshStandardMaterial({ color: 0x6b4423, roughness: 1 });
  const katto = new THREE.Mesh(new THREE.ConeGeometry(0.9, 0.5, 4), puuMat);
  katto.position.set(0, 1.5, kaivoZ);
  katto.rotation.y = Math.PI / 4;
  katto.castShadow = true;
  group.add(katto);
  for (const [dx, dz] of [[0.5, 0.5], [-0.5, 0.5], [0.5, -0.5], [-0.5, -0.5]]) {
    const pilari = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.2, 8), puuMat);
    pilari.position.set(dx, 0.95, kaivoZ + dz);
    pilari.castShadow = true;
    group.add(pilari);
  }
  kaivoRunko.userData.kaivo = true;
  colliders.push(new THREE.Box3().setFromObject(kaivoRunko));
  const kaivoHitMeshes = [kaivoRunko];

  // ---- Perhoset (koristeelliset, eivät vaikuta pelilogiikkaan) -----------
  function luoPerhonen(vari) {
    const mat = new THREE.MeshStandardMaterial({ color: vari, side: THREE.DoubleSide, roughness: 0.5, emissive: vari, emissiveIntensity: 0.12 });
    const siipiGeom = new THREE.PlaneGeometry(0.16, 0.13);
    const ryhma = new THREE.Group();
    const vasen = new THREE.Mesh(siipiGeom, mat);
    vasen.position.x = -0.01;
    const oikea = new THREE.Mesh(siipiGeom, mat);
    oikea.position.x = 0.01;
    ryhma.add(vasen, oikea);
    return { ryhma, vasen, oikea };
  }
  const PERHOS_VARIT = [0xffb703, 0xffffff, 0xff7fb0, 0x8ecae6];
  const perhoset = [];
  for (let i = 0; i < 5; i++) {
    const perhonen = luoPerhonen(PERHOS_VARIT[i % PERHOS_VARIT.length]);
    group.add(perhonen.ryhma);
    perhoset.push({
      ...perhonen,
      keskusX: (Math.random() - 0.5) * (AITA_X * 1.5),
      keskusZ: (Math.random() - 0.5) * (AITA_Z * 1.5),
      vaihe: Math.random() * Math.PI * 2,
      nopeus: 0.35 + Math.random() * 0.25,
      sade: 1.6 + Math.random() * 2.2,
      korkeus: 1.1 + Math.random() * 1.1,
    });
  }
  function paivitaPerhoset(dt, t) {
    for (const p of perhoset) {
      const kulma = t * p.nopeus + p.vaihe;
      p.ryhma.position.set(
        p.keskusX + Math.cos(kulma) * p.sade,
        p.korkeus + Math.sin(kulma * 2.3) * 0.3,
        p.keskusZ + Math.sin(kulma) * p.sade * 0.7,
      );
      p.ryhma.rotation.y = -kulma + Math.PI / 2;
      const rapsutus = Math.sin(t * 16 + p.vaihe * 5) * 0.9;
      p.vasen.rotation.y = rapsutus;
      p.oikea.rotation.y = -rapsutus;
    }
  }

  // ---- Hiukkaset ------------------------------------------------------
  const hiukkasTekstuuri = luoHiukkasTekstuuri();
  const HIUKKAS_MAARA = 30;
  const hiukkaset = [];
  for (let i = 0; i < HIUKKAS_MAARA; i++) {
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: hiukkasTekstuuri, transparent: true, depthWrite: false }));
    spr.visible = false;
    group.add(spr);
    hiukkaset.push({ mesh: spr, vx: 0, vy: 0, vz: 0, elinaika: 0, kesto: 0 });
  }
  let hiukkasIdx = 0;
  function suihkuta(x, y, z, vari, maara = 8) {
    for (let i = 0; i < maara; i++) {
      const h = hiukkaset[hiukkasIdx];
      hiukkasIdx = (hiukkasIdx + 1) % hiukkaset.length;
      h.mesh.position.set(x, y, z);
      h.mesh.material.color.set(vari);
      h.mesh.material.opacity = 1;
      h.mesh.visible = true;
      const koko = 0.14 + Math.random() * 0.14;
      h.mesh.scale.set(koko, koko, 1);
      const kulma = Math.random() * Math.PI * 2;
      const nopeus = 1 + Math.random() * 1.5;
      h.vx = Math.cos(kulma) * nopeus;
      h.vz = Math.sin(kulma) * nopeus;
      h.vy = 1.5 + Math.random() * 1.5;
      h.kesto = 0.5 + Math.random() * 0.3;
      h.elinaika = 0;
    }
  }
  function paivitaHiukkaset(dt) {
    for (const h of hiukkaset) {
      if (!h.mesh.visible) continue;
      h.elinaika += dt;
      if (h.elinaika >= h.kesto) { h.mesh.visible = false; continue; }
      h.vy -= 4 * dt;
      h.mesh.position.x += h.vx * dt;
      h.mesh.position.y += h.vy * dt;
      h.mesh.position.z += h.vz * dt;
      h.mesh.material.opacity = 1 - h.elinaika / h.kesto;
    }
  }

  // ---- Väinö (NPC, tarina/tehtävät) --------------------------------------
  function luoTekstiSprite(teksti, { fontti = '600 48px sans-serif', vari = '#eef1f5', ääriviiva = '#00000099' } = {}) {
    const cnv = document.createElement('canvas');
    const ctx = cnv.getContext('2d');
    ctx.font = fontti;
    const leveys = Math.ceil(ctx.measureText(teksti).width) + 24;
    cnv.width = leveys; cnv.height = 64;
    ctx.font = fontti;
    ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
    ctx.lineWidth = 6; ctx.strokeStyle = ääriviiva;
    ctx.strokeText(teksti, leveys / 2, 32);
    ctx.fillStyle = vari;
    ctx.fillText(teksti, leveys / 2, 32);
    const tex = new THREE.CanvasTexture(cnv);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: true });
    mat.toneMapped = false;
    const spr = new THREE.Sprite(mat);
    spr.scale.set(0.5 * (leveys / 64), 0.5, 1);
    return spr;
  }

  function luoVaino() {
    const g = new THREE.Group();
    const vartaloMat = new THREE.MeshStandardMaterial({ color: 0x5c6b47, roughness: 0.9 });
    const vartalo = new THREE.Mesh(new THREE.CapsuleGeometry(0.28, 0.55, 4, 8), vartaloMat);
    vartalo.position.y = 0.6;
    vartalo.castShadow = true;
    g.add(vartalo);

    const paaMat = new THREE.MeshStandardMaterial({ color: 0xe0b48c, roughness: 0.8 });
    const paa = new THREE.Mesh(new THREE.SphereGeometry(0.22, 12, 10), paaMat);
    paa.position.y = 1.15;
    paa.castShadow = true;
    g.add(paa);

    const hiusMat = new THREE.MeshStandardMaterial({ color: 0xd8d8d8, roughness: 1 });
    const parta = new THREE.Mesh(new THREE.SphereGeometry(0.15, 10, 8), hiusMat);
    parta.position.set(0, 1.03, 0.08);
    parta.scale.set(1, 0.8, 0.9);
    g.add(parta);

    const hattuMat = new THREE.MeshStandardMaterial({ color: 0x3d5a3d, roughness: 0.9 });
    const hattu = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.23, 0.12, 12), hattuMat);
    hattu.position.y = 1.32;
    hattu.castShadow = true;
    g.add(hattu);

    const keppiMat = new THREE.MeshStandardMaterial({ color: 0x5a3d22, roughness: 1 });
    const keppi = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.9, 6), keppiMat);
    keppi.position.set(0.3, 0.45, 0.1);
    keppi.rotation.z = 0.15;
    keppi.castShadow = true;
    g.add(keppi);

    // Isompi ja korkeampi kuin näkyvä vartalo — pelaajan silmäkorkeus (~1.6) on
    // lähes Väinön päälaen tasolla, jolloin täysin suora (ei-alaspäin) katse
    // ampui hänen ylitseen ennen tätä korjausta. Anna reilusti vertikaalista varaa.
    const hitBox = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.5, 2.0, 8),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    hitBox.position.y = 1.0;
    hitBox.userData.vaino = true;
    g.add(hitBox);

    // Himmeämpi kuin puhdas valkoinen, jotta bloom ei polta tekstiä ylivalottuneeksi.
    const nimikyltti = luoTekstiSprite('Väinö', { vari: '#c9cdd2' });
    nimikyltti.position.set(0, 1.55, 0);
    g.add(nimikyltti);

    const tehtavamerkki = luoTekstiSprite('!', { fontti: '800 64px sans-serif', vari: '#ffcf3d', ääriviiva: '#5a3d00' });
    tehtavamerkki.position.set(0, 1.95, 0);
    tehtavamerkki.visible = false;
    g.add(tehtavamerkki);

    return { ryhma: g, hitBox, tehtavamerkki };
  }
  const vaino = luoVaino();
  vaino.ryhma.position.set(1.7, 0, kaivoZ + 0.2);
  vaino.ryhma.rotation.y = -0.5;
  group.add(vaino.ryhma);
  const vainoHitMeshes = [vaino.hitBox];

  // ---- Pelitila ---------------------------------------------------------
  const peli = { tila: 'valmis', pisteet: 0, seuraavaSpawn: 1.5 };
  let vesiKapasiteetti = VESI_MAARA_ALKU;
  let vesiJaljella = vesiKapasiteetti;
  let taytonEdistyminen = 0;
  let aktiivinenTyokalu = 'kannu';
  let toimiPohjassa = false;
  let pakotettuKohde = null; // testaushaenta: ohittaa raycastin kun asetettu (ks. debug alla)

  // Tehtäväketju: ei kelloa, ei häviötä — questIndex kasvaa 0..TEHTAVAT.length
  // kun pelaaja käy Väinön luona hyväksymässä valmiin tehtävän palkinnon.
  let questIndex = 0;
  let questEdistyminen = 0;
  let questValmis = false;

  const raycaster = new THREE.Raycaster();
  const naytonKeski = new THREE.Vector2(0, 0);

  function kohde(camera, playerPos) {
    if (pakotettuKohde !== null) return pakotettuKohde;
    raycaster.setFromCamera(naytonKeski, camera);
    const osumat = raycaster.intersectObjects(bedHitMeshes.concat(kaivoHitMeshes, vainoHitMeshes), false);
    for (const o of osumat) {
      if (o.distance > TAHTAYS_ETAISYYS) break;
      if (o.object.userData.kaivo) return { tyyppi: 'kaivo' };
      if (o.object.userData.vaino) return { tyyppi: 'vaino' };
      if (o.object.userData.penkki) return { tyyppi: 'penkki', penkki: o.object.userData.penkki };
    }
    // Tähtäin ei osunut mihinkään — jos seisot aivan penkin/kaivon/Väinön vieressä, se
    // kelpaa silti kohteeksi. Aivan lähellä joutuisi muuten katsomaan hankalasti suoraan
    // alas, mikä tuntui siltä ettei toiminta tee mitään.
    if (!playerPos) return null;
    let lahin = null, lahinD = LAHIETAISYYS;
    for (const p of penkit) {
      if (p.tila !== 'kasvaa' || !p.ongelma) continue;
      const d = Math.hypot(p.ryhma.position.x - playerPos.x, p.ryhma.position.z - playerPos.z);
      if (d < lahinD) { lahin = p; lahinD = d; }
    }
    if (lahin) return { tyyppi: 'penkki', penkki: lahin };
    // Kaivo ja Väinö ovat vierekkäin — jos seisot molempien lähellä, lähin voittaa
    // (ei aina kaivo, kuten aiemmin kiinteä tarkistusjärjestys aiheutti).
    const dKaivo = Math.hypot(kaivoRunko.position.x - playerPos.x, kaivoRunko.position.z - playerPos.z);
    const dVaino = Math.hypot(vaino.ryhma.position.x - playerPos.x, vaino.ryhma.position.z - playerPos.z);
    const kaivoOk = dKaivo < LAHIETAISYYS;
    const vainoOk = dVaino < VAINO_LAHIETAISYYS;
    if (kaivoOk && vainoOk) return dKaivo <= dVaino ? { tyyppi: 'kaivo' } : { tyyppi: 'vaino' };
    if (kaivoOk) return { tyyppi: 'kaivo' };
    if (vainoOk) return { tyyppi: 'vaino' };
    return null;
  }

  function maxYhtaaikaiset() { return MAX_YHTAAIKAISET; }
  function seuraavaSpawnVali() { return SPAWN_VALI_MIN + Math.random() * (SPAWN_VALI_MAX - SPAWN_VALI_MIN); }
  function aloitaOngelma(p) {
    // Nurkan "mummon suosikki" -penkki vetää tomaattiensa vuoksi aina tuholaisia —
    // teemaan sopiva erikoistapaus, takaa myös että tehtävä 2 etenee kohtuuajassa.
    const pakotettu = p === penkit[VAINO_PENKKI_IDX] ? 'tuholainen' : null;
    p.ongelma = pakotettu || ONGELMATYYPIT[Math.floor(Math.random() * ONGELMATYYPIT.length)];
    p.ongelmaMax = RATKAISUAIKA[p.ongelma];
    p.ongelmaAika = 0;
    p.edistyminen = 0;
  }
  function paivitaSpawnaus(dt) {
    peli.seuraavaSpawn -= dt;
    if (peli.seuraavaSpawn > 0) return;
    const aktiiviset = penkit.filter(p => p.tila === 'kasvaa' && p.ongelma !== null).length;
    if (aktiiviset >= maxYhtaaikaiset()) { peli.seuraavaSpawn = 0.4; return; }
    const vapaat = penkit.filter(p => p.tila === 'kasvaa' && p.ongelma === null);
    if (vapaat.length === 0) { peli.seuraavaSpawn = 0.4; return; }
    aloitaOngelma(vapaat[Math.floor(Math.random() * vapaat.length)]);
    peli.seuraavaSpawn = seuraavaSpawnVali();
  }

  // ---- Tehtäväetenemä -----------------------------------------------------
  function paivitaTehtavanEdistyminenRatkaisussa(p, tyyppi) {
    if (questIndex >= TEHTAVAT.length || questValmis) return;
    const q = TEHTAVAT[questIndex];
    if (q.tyyppi === 'ratkaisuja') {
      questEdistyminen++;
    } else if (q.tyyppi === 'suojattuPenkki') {
      if (p === penkit[VAINO_PENKKI_IDX] && tyyppi === 'tuholainen') questEdistyminen++;
    } else if (q.tyyppi === 'kasteluja') {
      if (tyyppi === 'kuiva') questEdistyminen++;
    }
    if (q.maara && questEdistyminen >= q.maara) merkitseTehtavaValmiiksi();
  }
  function paivitaKaikkiKunnossaTarkistus() {
    if (questIndex >= TEHTAVAT.length || questValmis) return;
    if (TEHTAVAT[questIndex].tyyppi !== 'kaikkiKunnossa') return;
    if (penkit.every(p => p.tila === 'kasvaa' && p.ongelma === null)) merkitseTehtavaValmiiksi();
  }
  function merkitseTehtavaValmiiksi() {
    questValmis = true;
    audio.piippaa(660, 0.12, 'sine', 0.12);
  }

  function ratkaisePenkki(p) {
    const tyyppi = p.ongelma;
    peli.pisteet += PISTEET[tyyppi];
    if (peli.pisteet > tallennus.get('ennatys')) tallennus.set('ennatys', peli.pisteet);
    audio.AANIFUNKTIOT[tyyppi]();
    const varit = { kuiva: 0x4aa3e8, rikkaruoho: 0x8a6d2a, tuholainen: 0x2b2b2b };
    suihkuta(p.ryhma.position.x, 0.5, p.ryhma.position.z, varit[tyyppi], 8);
    if (tyyppi === 'kuiva') vesiJaljella = Math.max(0, vesiJaljella - 1);
    paivitaTehtavanEdistyminenRatkaisussa(p, tyyppi);
    p.ongelma = null;
    p.edistyminen = 0;
  }
  function kuolePenkki(p) {
    p.tila = 'kuollut';
    p.ongelma = null;
    p.edistyminen = 0;
    audio.kuolemaAani();
    suihkuta(p.ryhma.position.x, 0.4, p.ryhma.position.z, 0x8a6d3a, 10);
    p.uudelleenkasvyAika = UUDELLEENKASVYAIKA;
    // Ei elämiä, ei peli-ohi-tilaa — kuollut kasvi on pieni takaisku, penkki uudistuu
    // itsestään. Tämä ei ole selviytymispeli.
  }
  function uudistaPenkki(p) {
    p.tila = 'kasvaa';
    p.ongelma = null;
    p.ongelmaAika = 0;
    p.edistyminen = 0;
    p.kuivaVis = 0;
    p.rikkaVis = 0;
  }
  function paivitaPenkki(p, dt, tyostetaanko) {
    if (p.tila === 'kuollut') {
      p.uudelleenkasvyAika -= dt;
      if (p.uudelleenkasvyAika <= 0) uudistaPenkki(p);
      return;
    }
    if (p.ongelma === null) return;
    p.ongelmaAika += dt;
    if (tyostetaanko) {
      p.edistyminen += dt / TYON_KESTO[p.ongelma];
      if (p.edistyminen >= 1) { ratkaisePenkki(p); return; }
    } else {
      p.edistyminen = Math.max(0, p.edistyminen - dt * RAPAUTUMISNOPEUS);
    }
    if (p.ongelmaAika >= p.ongelmaMax) kuolePenkki(p);
  }

  const KUIVA_VARI_KUIVA = new THREE.Color(0x8a6d3a);
  const KUIVA_VARI_TUORE = new THREE.Color(0x4c8c3b);
  const SOIL_VARI_KUIVA = new THREE.Color(0xb79a6b);
  const SOIL_VARI_TUORE = new THREE.Color(0x5b3d24);
  const vari = new THREE.Color();

  function paivitaPenkinVisuaali(p, dt, t, camera) {
    const kuivaTavoite = (p.tila === 'kasvaa' && p.ongelma === 'kuiva') ? Math.min(1, p.ongelmaAika / p.ongelmaMax) : 0;
    p.kuivaVis = lahesty(p.kuivaVis, kuivaTavoite, 1.5, dt);
    const rikkaTavoite = (p.tila === 'kasvaa' && p.ongelma === 'rikkaruoho') ? Math.min(1, p.ongelmaAika / p.ongelmaMax) : 0;
    p.rikkaVis = lahesty(p.rikkaVis, rikkaTavoite, 1.2, dt);

    vari.copy(KUIVA_VARI_TUORE).lerp(KUIVA_VARI_KUIVA, p.kuivaVis);
    p.kasvi.lehtiMat.color.copy(vari);
    p.kasvi.ryhma.scale.y = p.tila === 'kuollut' ? 0.15 : 1 - 0.35 * p.kuivaVis;
    p.kasvi.ryhma.rotation.z = p.tila === 'kuollut' ? 0.6 : 0.25 * p.kuivaVis;

    const rv = p.tila === 'kasvaa' ? Math.max(0.001, p.rikkaVis) : 0.001;
    p.rikkaruoho.scale.set(rv, rv, rv);

    p.tuholainen.visible = p.tila === 'kasvaa' && p.ongelma === 'tuholainen';
    if (p.tuholainen.visible) {
      p.tuholainen.position.y = 0.35 + Math.sin(t * 10 + p.satunnaisvaihe) * 0.05;
      p.tuholainen.rotation.y += dt * 3;
    }

    const ongelmaAktiivinen = p.tila === 'kasvaa' && p.ongelma !== null;
    p.ongelmaIkoni.ryhma.visible = ongelmaAktiivinen;
    if (ongelmaAktiivinen) {
      p.ongelmaIkoni.kannu.visible = p.ongelma === 'kuiva';
      p.ongelmaIkoni.sakset.visible = p.ongelma === 'rikkaruoho';
      p.ongelmaIkoni.suihke.visible = p.ongelma === 'tuholainen';
      // Spritet kääntyvät kameraan automaattisesti — vain hento leijuntaliike tarpeen.
      p.ongelmaIkoni.ryhma.position.y = 1.7 + Math.sin(t * 3 + p.satunnaisvaihe) * 0.08;
    }

    p.palkki.ryhma.visible = ongelmaAktiivinen;
    if (ongelmaAktiivinen) {
      tahtaaPelaajaan(p.palkki.ryhma, p.ryhma.position, camera);
      p.palkki.taytto.scale.x = Math.max(0.001, Math.min(1, p.edistyminen));
      const vaara = Math.min(1, p.ongelmaAika / p.ongelmaMax);
      p.palkki.tausta.material.color.setRGB(0.13 + vaara * 0.55, 0.13 * (1 - vaara * 0.6), 0.13 * (1 - vaara * 0.6));
    }

    vari.copy(SOIL_VARI_TUORE).lerp(SOIL_VARI_KUIVA, p.kuivaVis * 0.6);
    if (p.tila === 'kuollut') vari.setHex(0x3a2a1a);
    p.maaMat.color.copy(vari);
  }

  // ---- HUD ---------------------------------------------------------------
  const hudPisteet = document.getElementById('hud-pisteet');
  const hudEnnatys = document.getElementById('hud-ennatys');
  const hudVesi = document.getElementById('hud-vesi');
  const hudTyokalu = document.getElementById('hud-tyokalu');
  const hudTehtavaOtsikko = document.getElementById('hud-tehtava-otsikko');
  const hudTehtavaTavoite = document.getElementById('hud-tehtava-tavoite');
  const hudTehtavaEdistyminen = document.getElementById('hud-tehtava-edistyminen');

  // Kosketuksen työkalunvaihtonapit (ei osa _template-fps-pohjaa — pelikohtainen HUD).
  // Nappia PITÄMÄLLÄ (ei vain napauttamalla) se ETTÄ vaihtaa työkalun JA työstää
  // tähtäimessä/lähellä olevaa kohdetta samalla kosketuksella — erillistä TOIMI-
  // nappia ei tarvita kasvien hoitoon, koska "pidä oikeaa työkalua pohjassa
  // kasvin lähellä" on nopeampi ele kuin "vaihda työkalu, sitten paina TOIMIa".
  // TOIMI-nappi (ks. _template-fps) on edelleen se joka puhuu Väinölle/täyttää
  // kaivon — työkalunapit EIVÄT koskaan kohdista Väinöön (ks. onToolButtonDown),
  // koska Väinö ja kaivo ovat niin lähellä toisiaan ja kasvipenkkejä että väljä
  // "puhu Väinölle" -läheisyys nappasi kasvinhoitoyrityksen ennen kuin ehti
  // löytää oikean penkin — tuntui siltä että kastelu "ei tee mitään".
  function onToolButtonDown(nimi) {
    vaihdaTyokalu(nimi);
    toimiPohjassa = true;
    if (peli.tila !== 'kaynnissa' || !viimeisinPelaaja) return;
    const osuma = kohde(viimeisinPelaaja.camera, viimeisinPelaaja.position);
    if (osuma && osuma.tyyppi === 'vaino') return; // työkalunapit eivät puhu Väinölle
    if (!voikoToimia(osuma)) audio.piippaa(160, 0.09, 'square', 0.07);
  }

  const tyokalunappiElementit = {
    kannu: document.getElementById('tyokalunappi-kannu'),
    sakset: document.getElementById('tyokalunappi-sakset'),
    suihke: document.getElementById('tyokalunappi-suihke'),
  };
  for (const [nimi, el] of Object.entries(tyokalunappiElementit)) {
    if (!el) continue;
    el.addEventListener('touchstart', (e) => {
      e.preventDefault(); e.stopPropagation();
      onToolButtonDown(nimi);
    }, { passive: false });
    const lopeta = (e) => { e.preventDefault(); e.stopPropagation(); onPrimaryUp(); };
    el.addEventListener('touchend', lopeta);
    el.addEventListener('touchcancel', lopeta);
  }
  const crosshair = document.getElementById('crosshair');
  const gateOtsikko = document.getElementById('gate-otsikko');
  const gateAla = document.getElementById('gate-alaotsikko');
  const vainoPuheEl = document.getElementById('vaino-puhe');
  let vainoPuheAjastin = null;

  function naytaPuhe(teksti) {
    if (!vainoPuheEl) return;
    vainoPuheEl.textContent = teksti;
    vainoPuheEl.classList.add('nakyy');
    clearTimeout(vainoPuheAjastin);
    vainoPuheAjastin = setTimeout(() => vainoPuheEl.classList.remove('nakyy'), 6000);
  }

  // Onko osuma (tähtäimen kohde) sellainen että toiminta juuri nyt tekisi jotain?
  // Yhteinen sekä crosshairin väriin että "ei vaikutusta" -äänipalautteeseen.
  function voikoToimia(osuma) {
    if (!osuma) return false;
    if (osuma.tyyppi === 'vaino') return true; // Väinölle voi aina puhua
    if (osuma.tyyppi === 'kaivo') return vesiJaljella < vesiKapasiteetti;
    if (osuma.tyyppi === 'penkki') {
      return !!(osuma.penkki.ongelma
        && TYOKALU_KOHTEELLE[osuma.penkki.ongelma] === aktiivinenTyokalu
        && (osuma.penkki.ongelma !== 'kuiva' || vesiJaljella > 0));
    }
    return false;
  }

  function puhuVainolle() {
    if (questIndex >= TEHTAVAT.length) {
      naytaPuhe('Kiitos avustasi — koko kylä muistaa tämän kesän.');
      return;
    }
    const q = TEHTAVAT[questIndex];
    if (!questValmis) {
      const maaraTeksti = q.maara ? ` (${questEdistyminen}/${q.maara})` : '';
      naytaPuhe(`${q.vainonSanat} ${q.tavoite}${maaraTeksti}.`);
      return;
    }
    audio.piippaa(784, 0.15, 'triangle', 0.14);
    if (q.tyyppi === 'kasteluja') {
      vesiKapasiteetti += VESI_KAPASITEETTI_KASVU;
      vesiJaljella = vesiKapasiteetti;
    }
    questIndex++;
    questEdistyminen = 0;
    questValmis = false;
    if (questIndex >= TEHTAVAT.length) {
      naytaPuhe(q.palkinto);
      peliValmistui();
    } else {
      const seuraava = TEHTAVAT[questIndex];
      naytaPuhe(`${q.palkinto} Seuraava: ${seuraava.otsikko} — ${seuraava.vainonSanat}`);
    }
  }

  function paivitaHud(osuma) {
    if (hudPisteet) hudPisteet.textContent = `Pisteet: ${peli.pisteet}`;
    if (hudEnnatys) hudEnnatys.textContent = `Ennätys: ${tallennus.get('ennatys')}`;
    if (hudVesi) hudVesi.textContent = `💧 ${vesiJaljella}/${vesiKapasiteetti}`;
    if (hudTyokalu) hudTyokalu.textContent = TYOKALU_NIMI[aktiivinenTyokalu];
    for (const [nimi, el] of Object.entries(tyokalunappiElementit)) {
      el?.classList.toggle('valittu', nimi === aktiivinenTyokalu);
    }
    if (crosshair) crosshair.classList.toggle('kohde', voikoToimia(osuma));
    if (hudTehtavaOtsikko) {
      if (questIndex >= TEHTAVAT.length) {
        hudTehtavaOtsikko.textContent = 'Puutarha kunnossa';
        hudTehtavaTavoite.textContent = 'Kaikki tehtävät suoritettu.';
        hudTehtavaEdistyminen.textContent = '';
      } else {
        const q = TEHTAVAT[questIndex];
        hudTehtavaOtsikko.textContent = q.otsikko;
        hudTehtavaTavoite.textContent = q.tavoite;
        hudTehtavaEdistyminen.textContent = questValmis
          ? 'Valmis! Puhu Väinölle.'
          : (q.maara ? `${questEdistyminen}/${q.maara}` : '');
      }
    }
  }

  function vaihdaTyokalu(nimi) {
    if (!TYOKALU_NIMI[nimi] || nimi === aktiivinenTyokalu) return;
    aktiivinenTyokalu = nimi;
    audio.piippaa(520, 0.06, 'triangle', 0.1);
  }

  // Nimettyinä (ei anonyymeinä metodeina return-oliossa) jotta myös kosketuksen
  // työkalunapit voivat kutsua niitä suoraan — pitämällä työkalunappia pohjassa
  // sekä vaihtaa työkalun ETTÄ työstää samalla kosketuksella, ei tarvitse erikseen
  // vaihtaa työkalua ja sitten koskea erilliseen TOIMI-nappiin.
  function onPrimaryDown() {
    toimiPohjassa = true;
    if (peli.tila !== 'kaynnissa' || !viimeisinPelaaja) return;
    const osuma = kohde(viimeisinPelaaja.camera, viimeisinPelaaja.position);
    if (osuma && osuma.tyyppi === 'vaino') { puhuVainolle(); return; }
    // Heti klikatessa kuuluva vahvistus siitä ETTÄ jotain tapahtui — ilman tätä
    // väärällä työkalulla/tyhjällä kannulla klikkaaminen tuntuu siltä ettei mikään reagoi.
    if (!voikoToimia(osuma)) audio.piippaa(160, 0.09, 'square', 0.07);
  }
  function onPrimaryUp() { toimiPohjassa = false; }

  function aloita() {
    audio.alustaAudio();
    peli.tila = 'kaynnissa';
    peli.pisteet = 0;
    peli.seuraavaSpawn = 1.5;
    questIndex = 0;
    questEdistyminen = 0;
    questValmis = false;
    vesiKapasiteetti = VESI_MAARA_ALKU;
    vesiJaljella = vesiKapasiteetti;
    taytonEdistyminen = 0;
    aktiivinenTyokalu = 'kannu';
    for (const p of penkit) uudistaPenkki(p);
    audio.kaynnistaAmbient();
    if (gateOtsikko) gateOtsikko.textContent = 'Puutarhurin Painajainen';
    if (gateAla) gateAla.textContent = `${TEHTAVAT[0].vainonSanat} ${TEHTAVAT[0].tavoite}.`;
  }

  function peliValmistui() {
    peli.tila = 'valmistui';
    audio.pysaytaAmbient();
    // Positiivinen "juhla"-äänisarja — nouseva kolmisointu, ei surullinen kuten
    // vanha selviytymispelin peliLoppuu-versio.
    audio.piippaa(523, 0.12, 'sine', 0.14);
    setTimeout(() => audio.piippaa(659, 0.12, 'sine', 0.14), 130);
    setTimeout(() => audio.piippaa(784, 0.18, 'sine', 0.16), 260);
    if (gateOtsikko) gateOtsikko.textContent = 'Puutarhajuhlat onnistuivat!';
    if (gateAla) gateAla.textContent = `Puutarha on kunnossa. Pisteet: ${peli.pisteet}  Ennätys: ${tallennus.get('ennatys')}`;
    document.exitPointerLock();
    hooks.onRequestExit?.(); // kosketuksella ei ole pointerlockchange-tapahtumaa
  }

  let kokonaisAika = 0;
  let viimeisinPelaaja = null;

  return {
    colliders,
    spawn: new THREE.Vector3(0, 0, (kaivoZ + alkuZ) / 2),
    yaw: Math.PI, // katso kaivolta kohti penkkejä (+Z), ei selin niihin

    onLockChange(locked) {
      if (locked && peli.tila !== 'kaynnissa') aloita();
    },
    onPrimaryDown,
    onPrimaryUp,
    onKeyDown(code) {
      if (code === 'Digit1') vaihdaTyokalu('kannu');
      if (code === 'Digit2') vaihdaTyokalu('sakset');
      if (code === 'Digit3') vaihdaTyokalu('suihke');
    },

    update(dt, ctx) {
      kokonaisAika += dt;
      paivitaHiukkaset(dt);
      paivitaPerhoset(dt, kokonaisAika);
      vaino.ryhma.position.y = Math.sin(kokonaisAika * 1.2) * 0.015;
      vaino.tehtavamerkki.visible = questIndex < TEHTAVAT.length && questValmis;
      if (vaino.tehtavamerkki.visible) {
        vaino.tehtavamerkki.position.y = 1.95 + Math.sin(kokonaisAika * 3) * 0.05;
      }

      if (peli.tila !== 'kaynnissa' || !ctx.locked) return;
      paivitaSpawnaus(dt);
      paivitaKaikkiKunnossaTarkistus();

      const camera = ctx.player.camera;
      viimeisinPelaaja = ctx.player;
      const osuma = kohde(camera, ctx.player.position);

      for (const p of penkit) {
        const tyostetaanko = !!(osuma && osuma.tyyppi === 'penkki' && osuma.penkki === p
          && toimiPohjassa && TYOKALU_KOHTEELLE[p.ongelma] === aktiivinenTyokalu
          && (p.ongelma !== 'kuiva' || vesiJaljella > 0));
        paivitaPenkki(p, dt, tyostetaanko);
        paivitaPenkinVisuaali(p, dt, kokonaisAika, camera);
      }

      if (osuma && osuma.tyyppi === 'kaivo' && toimiPohjassa && vesiJaljella < vesiKapasiteetti) {
        taytonEdistyminen += dt / TAYTTOAIKA;
        if (taytonEdistyminen >= 1) {
          vesiJaljella = vesiKapasiteetti;
          taytonEdistyminen = 0;
          audio.piippaa(880, 0.15, 'sine', 0.12);
        }
      } else {
        taytonEdistyminen = Math.max(0, taytonEdistyminen - dt * 0.8);
      }

      paivitaHud(osuma);
    },

    // Testaus-/debug-koukku (ks. peli-testi-skilli) — ei osa pelin julkista rajapintaa.
    debug: {
      peli, penkit, tallennus, aloita, aloitaOngelma, ratkaisePenkki, vaihdaTyokalu, puhuVainolle,
      get vesiJaljella() { return vesiJaljella; },
      get vesiKapasiteetti() { return vesiKapasiteetti; },
      get aktiivinenTyokalu() { return aktiivinenTyokalu; },
      get questIndex() { return questIndex; },
      get questEdistyminen() { return questEdistyminen; },
      get questValmis() { return questValmis; },
      asetaToimi(v) { toimiPohjassa = v; },
      pakotaKohde(v) { pakotettuKohde = v; },
    },
  };
}
